








































































import Vue from "vue";
import { Word } from "@tt-app/types";
import { MicConfig } from "@joyz/app-core/utils/microphone";

import IconClose from "@joyz/app-core/assets/icons/black/Close.svg";
import IconSpeaker from "@joyz/app-core/assets/icons/default/Speaker.svg";

import TtMic from "@tt-app/components/exercise/TtMic.vue";
import {
  evaluatePronunciation,
  getAuthInfo,
  uploadPronunciationDrillAudioToS3
} from "@tt-app/utils";
import { IpaEvaluated } from "@joyz/app-core/models/typography";
import { api } from "@tt-app/api";
import { audioContext } from "@tt-app/utils/audio/audioContext";

interface Data {
  icons: { [key: string]: string };
  loading: boolean;
  practiceEvaluation?: any;
  recording: boolean;
  recordingTimeout: number;
  recordingTimeoutFunction?: number;
}

interface Methods {
  closeClickHandler: () => void;
  onMicStarted: () => void;
  onMicStopped: (blob: Blob[]) => void;
  onMicTimeout: () => void;
}

interface Computed {
  activeTabIndex: number;
  answerEvaluation: any;
  dictionaryTabTitle: string;
  defined: boolean;
  definition: any;
  exampleStress: any;
  noTabs: boolean;
  practiceEvaluationIpa: IpaEvaluated;
  practiceTabTitle: string;
  ipaNeutral: string;
  micConfig: MicConfig;
  userStress: any;
}

interface Props {
  currentProgress: number;
  dictionaryOnly: boolean;
  hideTabTitles: boolean;
  intonationCheck: any;
  logId: number;
  opened: boolean;
  exampleAudioButtonClickHandler: () => void;
  word?: Word;
}

export default Vue.extend<Data, Methods, Computed, Props>({
  name: "DictionarySentenceModal",
  components: {
    TtMic
  },
  props: {
    currentProgress: {
      type: Number,
      required: true
    },
    dictionaryOnly: {
      type: Boolean,
      default: true
    },
    hideTabTitles: {
      type: Boolean,
      default: false
    },
    intonationCheck: {
      type: Object,
      default: undefined
    },
    logId: {
      type: Number,
      default: undefined
    },
    opened: {
      type: Boolean,
      required: true
    },
    exampleAudioButtonClickHandler: {
      type: Function,
      required: true
    },
    word: {
      type: Object,
      default: undefined
    }
  },

  data() {
    return {
      icons: {
        close: IconClose,
        speaker: IconSpeaker
      },
      ipa: "",
      loading: false,
      practiceEvaluation: undefined,
      recording: false,
      recordingTimeout: 10000,
      recordingTimeoutFunction: undefined
    };
  },

  computed: {
    activeTabIndex() {
      return this.dictionaryOnly ? 1 : 0;
    },
    answerEvaluation() {
      if (!this.intonationCheck) return [];

      return this.intonationCheck.user.ipaAccuracy;
    },
    defined() {
      return this.definition?.length > 0;
    },
    definition() {
      if (!this.word) return [];

      return this.word.definition;
    },
    dictionaryTabTitle() {
      if (this.hideTabTitles) return "";

      return this.$__("modal.dictionary.tab.dictionary");
    },
    exampleStress() {
      if (!this.word) return [];

      const { stressedSyllables } = this.intonationCheck.example;

      return this.word.ipa.map((ipa: string, index: number) => {
        return {
          text: ipa,
          underlined: index >= stressedSyllables.start && index <= stressedSyllables.end
        };
      });
    },
    ipaNeutral() {
      if (!this.word || !this.word.ipa) return "";

      return this.word.ipa.join("");
    },
    micConfig() {
      return {
        impl: "ScriptNodeRecorder",
        options: {
          audioCtx: audioContext,
          onRecordingStarted: this.onMicStarted,
          onRecordingStopped: this.onMicStopped,
          queueIntervalPeriod: 1000
        }
      };
    },
    noTabs() {
      const dictionaryAvailable = this.dictionaryOnly && this.defined;
      let pronunciationAvailable = false;

      if (this.word && !this.dictionaryOnly) {
        pronunciationAvailable = this.word.pronunciationCardUuid !== null;
      }

      return !dictionaryAvailable && !pronunciationAvailable;
    },
    practiceEvaluationIpa() {
      if (!this.practiceEvaluation) return [];

      return this.practiceEvaluation.map(
        (e: any): IpaEvaluated => ({ text: e.ipa, isCorrect: e.isCorrect })
      );
    },
    practiceTabTitle() {
      if (this.hideTabTitles) return "";

      return this.$__("modal.dictionary.tab.practice");
    },
    userStress() {
      if (!this.word) return [];
      const { stressedSyllables } = this.intonationCheck.user;

      return this.word.ipa.map((ipa: string, index: number) => {
        return {
          text: ipa,
          highlighted: index >= stressedSyllables.start && index <= stressedSyllables.end
        };
      });
    }
  },
  methods: {
    closeClickHandler() {
      this.practiceEvaluation = undefined;
      this.loading = false;
      this.$emit("close");
    },
    onMicTimeout() {
      console.log("mic timeout");
    },
    onMicStarted() {
      const ttMic: any = this.$refs.microphone;
      const jzMic: any = ttMic.$refs.microphone;
      const micHandler = jzMic.mic;

      if (micHandler.errors.length > 0) {
        return;
      }

      const mic = this.$refs.microphone as any;

      this.recording = true;

      this.recordingTimeoutFunction = window.setTimeout(() => {
        if (mic && mic.$refs.microphone.mic.status === "recording") {
          mic.stopRecording();
        }
        this.$emit("recording-timeout");
      }, this.recordingTimeout);
    },
    async onMicStopped(audioBlob) {
      if (!this.opened) return;

      window.clearTimeout(this.recordingTimeoutFunction);

      this.recording = false;
      this.loading = true;
      if (!this.word) return;

      const firstBlob = audioBlob[0];

      if (firstBlob.size > 200) {
        try {
          this.practiceEvaluation = await evaluatePronunciation(
            firstBlob,
            this.word.sentenceSpelling,
            this.word.ipa
          );
        } catch (error) {
          this.practiceEvaluation = this.word.ipa.map(() => ({
            isCorrect: false
          }));
        }

        this.practiceEvaluation = this.word.ipa.map((ipa, index) => {
          return {
            ipa,
            isCorrect: this.practiceEvaluation[index].isCorrect
          };
        });

        const audioUrl = await uploadPronunciationDrillAudioToS3(firstBlob);

        const authInfo = await getAuthInfo(this.$store.getters);
        const practiceLogPayload = {
          ...authInfo,
          ...this.$route.params,
          exerciseType: 10,
          uuid: this.word.pronunciationCardUuid,
          evaluation: this.practiceEvaluation,
          logId: this.logId,
          audio: audioUrl,
          retry: 0
        };

        await api.exercise.submitSingleSentenceReadingPronunciationPractice(practiceLogPayload);
      }

      const mic = this.$refs.microphone;

      if (mic) {
        (mic as any).resetData();
      }
      this.loading = false;
    }
  }
});
